import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

const ENDPOINTS = {
  orders: Spree.routes.api_v2_storefront_account_orders_summary,
  recycling: Spree.routes.api_v2_storefront_account_recycling_history,
  total_recycling:
    Spree.routes.api_v2_storefront_account_total_recycling_history,
  certificate: Spree.routes.api_v2_storefront_account_recycling_history,
}

const EXPORT_ENDPOINTS = {
  recycling: Spree.routes.api_v2_storefront_account_recycling_history_export,
  total_recycling:
    Spree.routes.api_v2_storefront_account_total_recycling_history_export,
}

const DEFAULT_FILTER_RANGE = 'all'

const RANGE_FILTER_OPTIONS = [
  { value: 'last_30_days', translationKey: 'last_30_days' },
  { value: 'year_to_date', translationKey: 'year_to_date' },
  { value: 'all', translationKey: 'all_time' },
]

const cancelConfirmMessage = (order, translations) => {
  return `${translations.cancel_confirm_message} ${
    order.has_subscriptions ? translations.cancel_subscription_notice : ''
  }`.trim()
}

// Column-driven table definitions. Each column renders its own cell body
// (including any mobile-only label markup); `mobileLabel` is a shortcut for
// the common "label: value" mobile layout used by the recycling tables.
const TABLE_CONFIGS = {
  orders: {
    mobileHeadKey: 'completed_orders',
    rowKey: (order) => order.id,
    columns: [
      {
        key: 'order-date',
        className: 'order-date d-flex d-md-table-cell',
        headerKey: 'order_date',
        headerClassName: 'order-date',
        render: (order, translations) => (
          <>
            <div className="col-6 d-md-none">
              <b>{translations.order_date}:</b>
            </div>
            <div>{order.order_date}</div>
            <div className="d-none d-md-flex flex-column">
              {!order.po && (
                <>
                  <a
                    className="readMoreLink"
                    href={`/${SPREE_LOCALE}/invoice.pdf?order_id=${order.order_number}`}
                  >
                    {translations.download_receipt}
                  </a>
                  <a
                    className="readMoreLink text-nowrap"
                    data-method="post"
                    href={`/${SPREE_LOCALE}/orders/${order.order_number}/resend`}
                    rel="nofollow"
                  >
                    {translations.resend}
                  </a>
                </>
              )}
            </div>
          </>
        ),
      },
      {
        key: 'order-number',
        className: 'order-number d-flex d-md-table-cell',
        headerKey: 'order_num',
        headerClassName: 'order-number',
        render: (order, translations) => (
          <>
            <div className="col-6 d-md-none">
              <b>{translations.order_num}:</b>
            </div>
            <div>#{order.order_number}</div>
            <a
              className="readMoreLink d-none d-md-inline-block text-nowrap"
              href={`/${SPREE_LOCALE}/orders/${order.order_number}`}
            >
              {translations.view_order_summary}
            </a>
          </>
        ),
      },
      {
        key: 'order-total',
        className: 'order-total d-flex d-md-table-cell text-md-end',
        headerKey: 'total',
        headerClassName: 'order-total text-end',
        render: (order, translations) => (
          <>
            <div className="col-6 d-md-none">
              <b>{translations.total}:</b>
            </div>
            <span>{order.total}</span>
          </>
        ),
      },
      {
        key: 'order-shipping-status',
        className: 'order-shipping-status d-flex d-md-table-cell',
        headerKey: 'shipping_status',
        headerClassName: 'order-shipping-status',
        render: (order, translations) => (
          <>
            <div className="col-6 d-md-none">
              <b>{translations.shipping_status}:</b>
            </div>
            <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
              {(order.shipment_state || []).map((s) => (
                <li key={s.tracking_number || s.status}>
                  {s.tracking_number ? (
                    s.tracking_link ? (
                      <>
                        <a
                          href={s.tracking_link}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {s.tracking_number}
                        </a>
                        <br />({s.status})
                      </>
                    ) : (
                      <>
                        {s.tracking_number}
                        <br />({s.status})
                      </>
                    )
                  ) : (
                    s.status
                  )}
                </li>
              ))}
            </ul>
            {order.allow_cancel && (
              <a
                className="d-none d-md-inline"
                data-bs-target="#cancelOrderModal"
                data-bs-toggle="modal"
                data-cancel-order-number={order.order_number}
                data-cancel-url={`/${SPREE_LOCALE}/orders/${order.order_number}/cancel`}
                data-confirm-message={cancelConfirmMessage(order, translations)}
                href="#"
                onClick={(e) => e.preventDefault()}
              >
                {translations.cancel_order}
              </a>
            )}
          </>
        ),
      },
      {
        key: 'order-mobile-actions',
        className: 'd-md-none mt-4',
        render: (order, translations) => (
          <>
            <a
              className="buttonComponent--secondary"
              href={`/${SPREE_LOCALE}/orders/${order.order_number}`}
            >
              {translations.view_order_summary}
            </a>
            {order.allow_cancel && (
              <a
                className="buttonComponent--secondary cancel_order--mobile"
                data-bs-target="#cancelOrderModal"
                data-bs-toggle="modal"
                data-cancel-order-number={order.order_number}
                data-cancel-url={`/${SPREE_LOCALE}/orders/${order.order_number}/cancel`}
                data-confirm-message={cancelConfirmMessage(order, translations)}
                href="#"
                onClick={(e) => e.preventDefault()}
              >
                <span>{translations.cancel_order}</span>
              </a>
            )}
          </>
        ),
      },
    ],
  },

  recycling: {
    mobileHeadKey: 'recycling_history',
    rowKey: (item) => item.tracking_number,
    defaultSort: 'received_desc',
    columns: [
      {
        key: 'tracking-number',
        className: 'tracking-number',
        headerKey: 'tracking_num',
        headerClassName: 'tracking-number',
        render: (item) => item.tracking_number,
      },
      {
        key: 'product-name',
        className: 'product-name',
        headerKey: 'product_name',
        headerClassName: 'product-name',
        sort: { asc: 'product_name_asc', desc: 'product_name_desc' },
        render: (item) => item.product_name,
      },
      {
        key: 'weight',
        className: 'weight d-flex d-md-table-cell',
        headerKey: 'weight',
        headerClassName: 'weight',
        mobileLabel: 'weight',
        sort: { asc: 'weight_asc', desc: 'weight_desc' },
        render: (item) => `${item.weight} ${item.weight_unit}`,
      },
      {
        key: 'received-date',
        className: 'received-date d-flex d-md-table-cell',
        headerKey: 'received_date',
        headerClassName: 'received-date',
        mobileLabel: 'received_date',
        sort: { asc: 'received_asc', desc: 'received_desc' },
        render: (item) => item.received_date || '',
      },
    ],
  },

  total_recycling: {
    mobileHeadKey: 'total_recycling_history',
    rowKey: (item) => `${item.product_name}${item.size}`,
    defaultSort: 'product_name_asc',
    columns: [
      {
        key: 'product-name',
        className: 'product-name',
        headerKey: 'product_name',
        headerClassName: 'product-name',
        sort: { asc: 'product_name_asc', desc: 'product_name_desc' },
        render: (item) => `${item.product_name}${item.size}`,
      },
      {
        key: 'weight',
        className: 'weight d-flex d-md-table-cell',
        headerKey: 'weight',
        headerClassName: 'weight',
        mobileLabel: 'weight',
        sort: { asc: 'weight_asc', desc: 'weight_desc' },
        render: (item) => `${item.total_weight} ${item.total_weight_unit}`,
      },
      {
        key: 'total-shipments',
        className: 'total-shipments d-flex d-md-table-cell',
        headerKey: 'total_shipments',
        headerClassName: 'total-shipments',
        mobileLabel: 'total_shipments',
        sort: { asc: 'total_shipments_asc', desc: 'total_shipments_desc' },
        render: (item) => item.total_quantity,
      },
    ],
  },

  certificate: {
    mobileHeadKey: 'certificate_history',
    rowKey: (item) => item.tracking_number,
    columns: [
      {
        key: 'product-name',
        className: 'product-name',
        headerKey: 'product_name',
        headerClassName: 'product-name',
        render: (item) => item.product_name,
      },
      {
        key: 'tracking-number',
        className: 'tracking-number d-flex d-md-table-cell',
        headerKey: 'tracking_num',
        headerClassName: 'tracking-number',
        mobileLabel: 'tracking_num',
        render: (item) => item.tracking_number,
      },
      {
        key: 'check-in-date',
        className: 'received-date d-flex d-md-table-cell',
        headerKey: 'check_in_date',
        headerClassName: 'check-in-date',
        mobileLabel: 'check_in_date',
        render: (item) => item.received_date || '',
      },
      {
        key: 'certificate-request',
        className: 'certificate-request',
        headerKey: 'certificate_request',
        headerClassName: 'certificate-request',
        render: (item, translations) => (
          <a
            className="readMoreLink downloadCertificateLink"
            href={`/${SPREE_LOCALE}/account/certificate?package_id=${item.id}`}
          >
            {translations.download_certificate}
          </a>
        ),
      },
    ],
  },
}

const sortDirectionFor = (column, sortBy) => {
  if (!column.sort) return null
  if (sortBy === column.sort.asc) return 'asc'
  if (sortBy === column.sort.desc) return 'desc'
  return null
}

const ArrowIcon = ({ flipped }) => {
  return (
    <svg
      className={`sortIcon_arrow ${flipped ? 'sortIcon_arrow--down' : ''}`}
      fill="none"
      height="12"
      viewBox="0 0 24 24"
      width="12"
    >
      <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="12" x2="12" y1="23" y2="5" />
      <polyline
        fill="none"
        points="5 12 12 5 19 12"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  )
}

ArrowIcon.propTypes = {
  flipped: PropTypes.bool,
}

const SortNeutralIcon = () => (
  <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
    <path
      clipRule="evenodd"
      d="M0.572077 8.14401C0.262865 8.44072 0.252734 8.93192 0.549448 9.24113C0.846162 9.55035 1.33736 9.56048 1.64657 9.26376L5.78946 5.28832L5.78946 16.6829C5.78946 17.1114 6.13687 17.4588 6.56541 17.4588C6.99396 17.4588 7.34137 17.1114 7.34137 16.6829L7.34137 5.33522L11.4354 9.26376C11.7446 9.56048 12.2358 9.55035 12.5325 9.24113C12.8292 8.93192 12.8191 8.44072 12.5099 8.14401L7.07823 2.93189C6.778 2.6438 6.30396 2.6438 6.00373 2.93189L0.572077 8.14401Z"
      fill="currentColor"
      fillRule="evenodd"
    />
    <path
      clipRule="evenodd"
      d="M18.5931 18.3311L18.5931 6.98371C18.5931 6.55517 18.2457 6.20776 17.8172 6.20776C17.3886 6.20776 17.0412 6.55517 17.0412 6.98371L17.0412 18.3786L12.898 14.4028C12.5888 14.1061 12.0976 14.1163 11.8009 14.4255C11.5042 14.7347 11.5143 15.2259 11.8235 15.5226L17.2552 20.7347C17.5554 21.0228 18.0295 21.0228 18.3297 20.7347L23.7613 15.5226C24.0706 15.2259 24.0807 14.7347 23.784 14.4255C23.4873 14.1163 22.9961 14.1061 22.6868 14.4028L18.5931 18.3311Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
)

const SortIcon = ({ direction }) => {
  if (direction) {
    return (
      <span className="sortIcon sortIcon--active">
        <ArrowIcon flipped={direction === 'desc'} />
      </span>
    )
  }

  return (
    <span className="sortIcon">
      <SortNeutralIcon />
    </span>
  )
}

SortIcon.propTypes = {
  direction: PropTypes.oneOf(['asc', 'desc', null]),
}

const DownloadIcon = () => (
  <svg
    className="buttonIcon"
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
  >
    <path
      d="M11.0595 2.80851L11.0595 14.6322L6.7938 10.5389C6.47162 10.2297 5.95981 10.2403 5.65065 10.5625C5.34148 10.8847 5.35204 11.3965 5.67422 11.7056L11.3338 17.1364C11.6466 17.4366 12.1405 17.4366 12.4533 17.1364L18.1129 11.7056C18.4351 11.3965 18.4456 10.8847 18.1365 10.5625C17.8273 10.2403 17.3155 10.2297 16.9933 10.5389L12.6765 14.6812V2.80851C12.6765 2.36198 12.3145 2 11.868 2C11.4215 2 11.0595 2.36198 11.0595 2.80851Z"
      fill="currentColor"
    />
    <path
      d="M3.80851 19.383C3.36198 19.383 3 19.745 3 20.1915C3 20.638 3.36198 21 3.80851 21H19.9786C20.4251 21 20.7871 20.638 20.7871 20.1915C20.7871 19.745 20.4251 19.383 19.9786 19.383H3.80851Z"
      fill="currentColor"
    />
  </svg>
)

const FunnelStatusIcon = () => (
  <svg className="filterStatusIcon" fill="none" height="16" viewBox="0 0 32 32" width="16">
    <path
      d="M29.3333 4H2.66663L13.3333 16.6133V25.3333L18.6666 28V16.6133L29.3333 4Z"
      fill="currentColor"
    />
  </svg>
)

const ChevronDownIcon = () => (
  <svg
    className="rangeFilter_chevron"
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
  >
    <path
      d="M18.3809 8.31152L11.999 15.4375L5.61914 8.31152H18.3809Z"
      fill="currentColor"
      stroke="currentColor"
    />
  </svg>
)

const FilterIcon = () => (
  <svg
    className="buttonIcon"
    fill="none"
    height="16"
    viewBox="0 0 24 24"
    width="16"
  >
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="3" x2="21" y1="6" y2="6" />
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="3" x2="21" y1="12" y2="12" />
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="3" x2="21" y1="18" y2="18" />
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="16" x2="16" y1="3" y2="9" />
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="8" x2="8" y1="9" y2="15" />
    <line stroke="currentColor" strokeLinecap="round" strokeWidth="2" x1="12" x2="12" y1="15" y2="21" />
  </svg>
)

const TableHeader = ({ config, onSortChange, sortBy, translations }) => {
  return (
    <>
      <tr className="tableComponent_mobileHead">
        <th>{translations[config.mobileHeadKey]}</th>
      </tr>
      <tr className="tableComponent_desktopHead">
        {config.columns
          .filter((column) => column.headerKey)
          .map((column) => (
            <th className={column.headerClassName} key={column.key}>
              {column.sort ? (
                <button
                  className="sortableHeader"
                  onClick={() => onSortChange(column)}
                  type="button"
                >
                  {translations[column.headerKey]}
                  <SortIcon direction={sortDirectionFor(column, sortBy)} />
                </button>
              ) : (
                translations[column.headerKey]
              )}
            </th>
          ))}
      </tr>
    </>
  )
}

const TableRow = ({ config, item, translations }) => {
  return (
    <tr>
      {config.columns.map((column) => (
        <td className={column.className} key={column.key}>
          {column.mobileLabel && (
            <div className="d-md-none">
              <b>{translations[column.mobileLabel]}:&nbsp;</b>
            </div>
          )}
          {column.render(item, translations)}
        </td>
      ))}
    </tr>
  )
}

const tableConfigPropType = PropTypes.shape({
  mobileHeadKey: PropTypes.string.isRequired,
  rowKey: PropTypes.func.isRequired,
  defaultSort: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      className: PropTypes.string,
      headerKey: PropTypes.string,
      headerClassName: PropTypes.string,
      mobileLabel: PropTypes.string,
      sort: PropTypes.shape({
        asc: PropTypes.string.isRequired,
        desc: PropTypes.string.isRequired,
      }),
      render: PropTypes.func.isRequired,
    }),
  ).isRequired,
})

TableHeader.propTypes = {
  config: tableConfigPropType.isRequired,
  onSortChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  translations: PropTypes.object.isRequired,
}

TableRow.propTypes = {
  config: tableConfigPropType.isRequired,
  item: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
}

const AccountHistoryTable = ({ type, translations }) => {
  const endpoint = ENDPOINTS[type]
  const exportEndpoint = EXPORT_ENDPOINTS[type]
  const config = TABLE_CONFIGS[type]

  const containerRef = useRef(null)
  const hasCheckedInitialData = useRef(false)

  const [records, setRecords] = useState([])
  const [hasAnyData, setHasAnyData] = useState(true)
  const [pagination, setPagination] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tokenReady, setTokenReady] = useState(false)
  const [isActiveTab, setIsActiveTab] = useState(false)
  const [sortBy, setSortBy] = useState(config.defaultSort || null)
  const [filterRange, setFilterRange] = useState(DEFAULT_FILTER_RANGE)
  const [isDesktopFilterOpen, setIsDesktopFilterOpen] = useState(false)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const [pendingFilterRange, setPendingFilterRange] = useState(DEFAULT_FILTER_RANGE)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const exportEmailRef = useRef(null)
  const [exportStatus, setExportStatus] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)

  const openExportModal = () => {
    setIsMobileFilterOpen(false)
    setExportStatus(null)
    setIsExportModalOpen(true)
  }

  const closeExportModal = () => {
    setIsExportModalOpen(false)
  }

  const submitExport = (e) => {
    e.preventDefault()
    if (exportLoading) return

    setExportLoading(true)
    setExportStatus(null)

    const body = { email: exportEmailRef.current.value }
    if (sortBy) body.sort_by = sortBy
    if (type === 'recycling') body.filter_range = filterRange

    fetch(exportEndpoint, {
      method: 'POST',
      headers: SpreeAPI.prepareHeaders(),
      credentials: 'include',
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`server responded with ${res.status}`)

        setExportStatus('success')
      })
      .catch((error) => {
        console.error('Recycling export failed:', error)
        setExportStatus('error')
      })
      .finally(() => setExportLoading(false))
  }

  const fetchPage = (page, append) => {
    setLoading(true)

    const params = new URLSearchParams({ page })
    if (sortBy) params.set('sort_by', sortBy)
    if (type === 'recycling') params.set('filter_range', filterRange)

    fetch(`${endpoint}?${params.toString()}`, {
      method: 'GET',
      headers: SpreeAPI.prepareHeaders(),
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((json) => {
        setRecords((prev) =>
          append ? prev.concat(json.records) : json.records,
        )
        setPagination(json.pagination)

        if (!hasCheckedInitialData.current) {
          hasCheckedInitialData.current = true
          setHasAnyData(json.records.length > 0)
        }
      })
      .finally(() => setLoading(false))
  }

  const handleSortChange = (column) => {
    const current = sortDirectionFor(column, sortBy)
    const next = current === 'asc' ? column.sort.desc : column.sort.asc

    setSortBy(next)
    setPagination(null)
    setRecords([])
  }

  const applyFilterRange = (value) => {
    if (value === filterRange) return

    setFilterRange(value)
    setPagination(null)
    setRecords([])
  }

  const handleDesktopFilterSelect = (value) => {
    applyFilterRange(value)
    setIsDesktopFilterOpen(false)
  }

  const handleDesktopClearFilters = () => {
    applyFilterRange(DEFAULT_FILTER_RANGE)
    setIsDesktopFilterOpen(false)
  }

  const openMobileFilter = () => {
    setPendingFilterRange(filterRange)
    setIsExportModalOpen(false)
    setIsMobileFilterOpen(true)
  }

  const handleMobileOptionSelect = (value) => {
    setPendingFilterRange(value)
  }

  const handleMobileClearAll = () => {
    applyFilterRange(DEFAULT_FILTER_RANGE)
    setIsMobileFilterOpen(false)
  }

  const handleMobileSeeResults = () => {
    applyFilterRange(pendingFilterRange)
    setIsMobileFilterOpen(false)
  }

  const activeFilterCount = filterRange !== DEFAULT_FILTER_RANGE ? 1 : 0
  const currentRangeOption = RANGE_FILTER_OPTIONS.find(
    (option) => option.value === filterRange,
  )

  useEffect(() => {
    const i = setInterval(() => {
      if (SpreeAPI && SpreeAPI.oauthToken) {
        setTokenReady(true)
        clearInterval(i)
      }
    }, 100)
    return () => clearInterval(i)
  }, [])

  useEffect(() => {
    const pane = containerRef.current && containerRef.current.closest('.tab-pane')

    if (!pane || pane.classList.contains('active')) {
      setIsActiveTab(true)
      return
    }

    const observer = new MutationObserver(() => {
      if (pane.classList.contains('active')) {
        setIsActiveTab(true)
        observer.disconnect()
      }
    })
    observer.observe(pane, { attributes: true, attributeFilter: ['class'] })

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!tokenReady) return
    if (!isActiveTab) return
    if (pagination) return
    fetchPage(1, false)
  }, [tokenReady, isActiveTab, pagination, type])

  useEffect(() => {
    if (!isMobileFilterOpen && !isExportModalOpen) return

    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [isMobileFilterOpen, isExportModalOpen])

  useEffect(() => {
    if (typeof initTooltip === 'function') initTooltip()
  }, [])

  const canLoadMore = pagination && pagination.page < pagination.total_pages

  const loadMore = () => {
    if (loading) return
    fetchPage(pagination.page + 1, true)
  }

  let content

  if (!pagination) {
    content = (
      <div className="loader_container">
        <div className="loader left-align">
          <div className="loader_spinner"></div>
        </div>
      </div>
    )
  } else if (records.length === 0) {
    content = <div>{translations[`empty_${type}`]}</div>
  } else {
    content = (
      <>
      <div className="tableComponent tableComponent_oneColumnMobile">
        <div
          className="custom-scroll"
          data-simplebar
          data-simplebar-auto-hide="false"
        >
          <table className="tableComponent_table order-summary">
            <thead className="active">
              <TableHeader
                config={config}
                onSortChange={handleSortChange}
                sortBy={sortBy}
                translations={translations}
              />
            </thead>
            <tbody>
              {records.map((item) => (
                <TableRow
                  config={config}
                  item={item}
                  key={config.rowKey(item)}
                  translations={translations}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {canLoadMore && (
        <div className="col-md-4 col-lg-3 mb-4 m-auto">
          <button
            className="buttonComponent--secondary w-100"
            disabled={loading}
            onClick={() => loadMore()}
            type="button"
          >
            {translations.load_more}
          </button>
        </div>
      )}
      </>
    )
  }

  return (
    <div ref={containerRef}>
      {exportEndpoint && hasAnyData && (
        <div className="historyToolbar">
          <div className="historyToolbar_desktop d-none d-md-flex align-items-center">
            {type === 'recycling' && (
              <>
                <FunnelStatusIcon />
                <span className="historyToolbar_status">
                  {activeFilterCount > 0
                    ? translations.active_filters_count.replace(
                        '%{count}',
                        activeFilterCount,
                      )
                    : translations.no_active_filters}
                </span>

                {activeFilterCount > 0 && (
                  <button
                    className="buttonComponent--secondary buttonComponent--medium historyToolbar_clear"
                    onClick={handleDesktopClearFilters}
                    type="button"
                  >
                    {translations.clear_filters}
                  </button>
                )}

                <div className="rangeFilter">
                  <button
                    className="rangeFilter_toggle"
                    onClick={() => setIsDesktopFilterOpen((open) => !open)}
                    type="button"
                  >
                    <span>
                      {translations.range_filter_title}
                      {currentRangeOption &&
                        `: ${translations[currentRangeOption.translationKey]}`}
                    </span>
                    <ChevronDownIcon />
                  </button>

                  {isDesktopFilterOpen && (
                    <ul className="rangeFilter_options">
                      {RANGE_FILTER_OPTIONS.map((option) => (
                        <li
                          className={`rangeFilter_option ${
                            option.value === filterRange ? 'active' : ''
                          }`}
                          key={option.value}
                        >
                          <button
                            onClick={() =>
                              handleDesktopFilterSelect(option.value)
                            }
                            type="button"
                          >
                            {translations[option.translationKey]}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            <div className="exportButton">
              <button
                className="buttonComponent--primary buttonComponent--medium exportButton_trigger"
                onClick={openExportModal}
                type="button"
              >
                <DownloadIcon />
                {translations.export}
              </button>

              {type === 'recycling' && (
                <button
                  className="tooltip-btn icon-tooltip-info"
                  data-bs-toggle="tooltip"
                  title={translations.export_tooltip}
                  type="button"
                />
              )}
            </div>
          </div>

          <div className="historyToolbar_mobile d-md-none">
            {type === 'recycling' && (
              <>
                <div className="historyToolbar_mobileStatus">
                  {activeFilterCount > 0 && <FunnelStatusIcon />}
                  <span className="historyToolbar_status">
                    {activeFilterCount > 0
                      ? translations.active_filters_count.replace(
                          '%{count}',
                          activeFilterCount,
                        )
                      : translations.no_active_filters}
                  </span>
                  {activeFilterCount > 0 && (
                    <button
                      className="buttonComponent--secondary buttonComponent--small historyToolbar_clear"
                      onClick={handleDesktopClearFilters}
                      type="button"
                    >
                      {translations.clear_filters}
                    </button>
                  )}
                </div>
                <button
                  className="buttonComponent--primary buttonComponent--medium w-100"
                  onClick={openMobileFilter}
                  type="button"
                >
                  <FilterIcon />
                  {translations.filters_button}
                </button>
              </>
            )}

            <button
              className="buttonComponent--primary buttonComponent--medium w-100"
              onClick={openExportModal}
              type="button"
            >
              <DownloadIcon />
              {translations.export}
            </button>
          </div>
        </div>
      )}

      {content}

      {isMobileFilterOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setIsMobileFilterOpen(false)}
          />
          <div className="modal fade show d-block" role="dialog" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    aria-label="Close"
                    className="closeModalButton"
                    onClick={() => setIsMobileFilterOpen(false)}
                    type="button"
                  >
                    <i className="icon-times"></i>
                  </button>

                  <h2 className="text-center mb-3">
                    {translations.range_filter_title}
                  </h2>

                  <ul className="rangeFilter_mobileList">
                    {RANGE_FILTER_OPTIONS.map((option) => (
                      <li
                        className={`rangeFilter_option ${
                          option.value === pendingFilterRange ? 'active' : ''
                        }`}
                        key={option.value}
                      >
                        <button
                          onClick={() =>
                            handleMobileOptionSelect(option.value)
                          }
                          type="button"
                        >
                          {translations[option.translationKey]}
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div className="modalBox_action historyModal_action d-flex flex-column">
                    <button
                      className="buttonComponent--primary mb-3"
                      onClick={handleMobileSeeResults}
                      type="button"
                    >
                      {translations.see_results}
                    </button>
                    <button
                      className="buttonComponent--secondary"
                      onClick={handleMobileClearAll}
                      style={{ marginLeft: 0 }}
                      type="button"
                    >
                      {translations.clear_all}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isExportModalOpen && (
        <>
          <div className="modal-backdrop fade show" onClick={closeExportModal} />
          <div className="modal fade show d-block" role="dialog" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    aria-label="Close"
                    className="closeModalButton"
                    onClick={closeExportModal}
                    type="button"
                  >
                    <i className="icon-times"></i>
                  </button>

                  <h2 className="text-center mb-3">{translations.export}</h2>

                  {exportStatus === 'success' ? (
                    <p className="exportModal_success">
                      {translations.export_success}
                    </p>
                  ) : (
                    <form className="bootstrapForm" onSubmit={submitExport}>
                      <div className="form-group exportModal_emailField">
                        <label htmlFor={`export-email-${type}`}>
                          {translations.export_email_label}
                        </label>
                        <input
                          className="form-control"
                          id={`export-email-${type}`}
                          multiple
                          placeholder={translations.export_email_placeholder}
                          ref={exportEmailRef}
                          required
                          type="email"
                        />
                      </div>

                      {exportStatus === 'error' && (
                        <p className="exportModal_error">
                          {translations.export_error}
                        </p>
                      )}

                      <div className="modalBox_action historyModal_action d-flex flex-column">
                        <button
                          className="buttonComponent--primary"
                          disabled={exportLoading}
                          type="submit"
                        >
                          {translations.export_submit}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

AccountHistoryTable.propTypes = {
  translations: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['orders', 'recycling', 'total_recycling', 'certificate'])
    .isRequired,
}

export default AccountHistoryTable
